<template>
  <div>
    <div v-if="loading" class="has-text-centered" style="padding: 30px">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <p>Cargando schedule...</p>
    </div>

    <div v-else-if="error" class="notification is-warning is-light">
      {{ error }}
    </div>

    <div v-else-if="!data || !data.schedule || !data.schedule.length">
      <p class="has-text-grey">Sin mensualidades programadas.</p>
    </div>

    <div v-else>
      <div class="columns is-multiline">
        <div class="column is-3">
          <div class="box has-text-centered" style="padding: 0.75rem">
            <p class="heading">Mensualidades</p>
            <p class="title is-5">{{ data.paidCount }}/{{ data.months }}</p>
          </div>
        </div>
        <div class="column is-3">
          <div class="box has-text-centered" style="padding: 0.75rem">
            <p class="heading">Mensualidad</p>
            <p class="title is-5">${{ formatMoney(data.monthlyAmount) }}</p>
          </div>
        </div>
        <div class="column is-3">
          <div class="box has-text-centered" style="padding: 0.75rem">
            <p class="heading">Total Pagado</p>
            <p class="title is-5 has-text-success">
              ${{ formatMoney(data.totalPaid) }}
            </p>
          </div>
        </div>
        <div class="column is-3">
          <div class="box has-text-centered" style="padding: 0.75rem">
            <p class="heading">Total Plan</p>
            <p class="title is-5">${{ formatMoney(data.total) }}</p>
          </div>
        </div>
      </div>

      <div v-if="data.setupInvoice" class="box">
        <p class="heading">Pago Inicial / Setup</p>
        <div class="columns is-vcentered">
          <div class="column is-3">
            <strong>${{ formatMoney(data.setupInvoice.amount) }} USD</strong>
          </div>
          <div class="column is-3">
            <b-tag :type="statusType(data.setupInvoice.status)">
              {{ statusLabel(data.setupInvoice.status) }}
            </b-tag>
          </div>
          <div class="column is-3">
            <span v-if="data.setupInvoice.paidDate" class="has-text-grey">
              {{ formatDate(data.setupInvoice.paidDate) }}
            </span>
          </div>
          <div class="column is-3 has-text-right">
            <a
              v-if="data.setupInvoice.hostedInvoiceUrl"
              :href="data.setupInvoice.hostedInvoiceUrl"
              target="_blank"
              class="button is-small is-info is-light"
            >
              Ver factura
            </a>
          </div>
        </div>
      </div>

      <b-table
        :data="data.schedule"
        :striped="true"
        :hoverable="true"
        :narrowed="true"
        :mobile-cards="true"
      >
        <b-table-column field="index" label="#" centered v-slot="props" width="60">
          <span class="tag is-primary">{{ props.row.index }}</span>
        </b-table-column>
        <b-table-column field="dueDate" label="Fecha de pago" centered v-slot="props">
          {{ formatDate(props.row.dueDate) }}
        </b-table-column>
        <b-table-column field="amount" label="Monto" numeric v-slot="props">
          ${{ formatMoney(props.row.amount) }}
        </b-table-column>
        <b-table-column field="status" label="Estado" centered v-slot="props">
          <b-tag :type="statusType(props.row.status)">
            {{ statusLabel(props.row.status) }}
          </b-tag>
        </b-table-column>
        <b-table-column field="paidDate" label="Fecha pagada" centered v-slot="props">
          <span v-if="props.row.paidDate">{{ formatDate(props.row.paidDate) }}</span>
          <span v-else class="has-text-grey">—</span>
        </b-table-column>
        <b-table-column field="amountPaid" label="Pagado" numeric v-slot="props">
          <span v-if="props.row.amountPaid > 0" class="has-text-success">
            ${{ formatMoney(props.row.amountPaid) }}
          </span>
          <span v-else class="has-text-grey">—</span>
        </b-table-column>
        <b-table-column label="Factura" centered v-slot="props">
          <a
            v-if="props.row.hostedInvoiceUrl"
            :href="props.row.hostedInvoiceUrl"
            target="_blank"
            class="button is-small is-info is-light"
          >
            <b-icon icon="external-link-alt" pack="fas" size="is-small"></b-icon>
          </a>
          <span v-else class="has-text-grey">—</span>
        </b-table-column>
      </b-table>

      <div
        v-if="data.subscription && !data.subscription.stripeSubscriptionId"
        class="notification is-info is-light"
        style="margin-top: 1rem"
      >
        Suscripción aún no activada. Estado: {{ data.subscription.status }}.
        <a
          v-if="data.subscription.checkoutUrl"
          :href="data.subscription.checkoutUrl"
          target="_blank"
        >Ir al link de pago</a>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
const API_URL = process.env.VUE_APP_API_URL;

export default {
  name: "FinancingScheduleTable",
  props: {
    contractId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      data: null,
    };
  },
  watch: {
    contractId: {
      immediate: true,
      handler(v) {
        if (v) this.load();
      },
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(
          `${API_URL}/financing/schedule/${this.contractId}`,
        );
        if (data.success) this.data = data.data;
        else this.error = data.error || data.message || "Error";
      } catch (err) {
        this.error = err?.response?.data?.error || err.message || "Error";
      } finally {
        this.loading = false;
      }
    },
    formatMoney(n) {
      const v = Number(n) || 0;
      return v.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(d) {
      if (!d) return "—";
      return moment.utc(d).format("DD/MM/YYYY");
    },
    statusType(s) {
      switch (s) {
        case "paid":
          return "is-success";
        case "open":
          return "is-warning";
        case "failed":
          return "is-danger";
        case "void":
          return "is-dark";
        case "pending":
        default:
          return "is-light";
      }
    },
    statusLabel(s) {
      switch (s) {
        case "paid":
          return "Pagado";
        case "open":
          return "Abierto";
        case "failed":
          return "Fallido";
        case "void":
          return "Anulado";
        case "uncollectible":
          return "Incobrable";
        case "pending":
        default:
          return "Pendiente";
      }
    },
  },
};
</script>
